import { useMemo, useState } from "react";
import Drawer from "./Drawer";
import Icon from "./Icon";
import SearchableSelect from "./SearchableSelect";
import { taskState, STATUS_LABELS, LEGEND_ENTRIES } from "../utils/taskCalendar";
import { todayKeyIst } from "../utils/formatters";

// Lists every task scheduled for a specific calendar day. Used as the overflow
// view when a calendar cell has too many tasks to fit inline.
export default function DayTasksDrawer({ open, date, tasks = [], onClose, onTaskClick }) {
  // Compare overdue against IST "today" rather than the browser's local
  // day, so dates line up consistently for staff outside India.
  const todayKey = todayKeyIst();

  const [search, setSearch] = useState("");
  const [statusSet, setStatusSet] = useState(() => new Set()); // empty = all
  const [employeeId, setEmployeeId] = useState("");
  const [businessId, setBusinessId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [masterId, setMasterId] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);

  // Unique facet options derived from the day's tasks — we only show a filter
  // dropdown when there's more than one distinct value to choose from, so the
  // bar stays uncluttered for small days.
  const facets = useMemo(() => {
    const emp = new Map();
    const biz = new Map();
    const grp = new Map();
    const mst = new Map();
    let unassignedSeen = false;
    for (const t of tasks) {
      if (t.employee?.id) emp.set(t.employee.id, t.employee.name);
      else unassignedSeen = true;
      // Each business *is* the client now; label it with the admin-entered
      // client_id when present so the dropdown reads "CL-0001 · Acme Co".
      if (t.business?.id) {
        const label = t.business.client_id
          ? `${t.business.client_id} · ${t.business.business_name}`
          : t.business.business_name;
        biz.set(t.business.id, label);
      }
      const g = t.task_master?.task_group;
      if (g?.id) grp.set(g.id, g.name);
      const m = t.task_master;
      if (m?.id) {
        // Show the group as a hint so "GSTR-3B" disambiguates across groups
        // when the same name happens to exist under different ones.
        const label = m.task_group?.name ? `${m.name} · ${m.task_group.name}` : m.name;
        mst.set(m.id, label);
      }
    }
    const employees = [...emp.entries()].map(([id, label]) => ({ id, label }));
    if (unassignedSeen) employees.unshift({ id: "__unassigned__", label: "— Unassigned —" });
    return {
      employees,
      businesses: [...biz.entries()].map(([id, label]) => ({ id, label })),
      groups: [...grp.entries()].map(([id, label]) => ({ id, label })),
      masters: [...mst.entries()].map(([id, label]) => ({ id, label })),
    };
  }, [tasks]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks.filter((t) => {
      const state = taskState(t);
      const dueKey = (t.due_date || "").slice(0, 10);
      const overdue =
        dueKey && dueKey < todayKey && t.status !== "completed" && t.status !== "cancelled";

      if (statusSet.size > 0 && !statusSet.has(state)) return false;
      if (overdueOnly && !overdue) return false;
      if (employeeId === "__unassigned__" && t.employee?.id) return false;
      if (employeeId && employeeId !== "__unassigned__" && String(t.employee?.id) !== String(employeeId)) return false;
      if (businessId && String(t.business?.id) !== String(businessId)) return false;
      if (groupId && String(t.task_master?.task_group?.id) !== String(groupId)) return false;
      if (masterId && String(t.task_master?.id) !== String(masterId)) return false;

      if (q) {
        const hay = [
          t.task_master?.name,
          t.business?.business_name,
          t.business?.client_id,
          t.employee?.name,
          t.task_master?.task_group?.name,
          t.remarks,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tasks, search, statusSet, employeeId, businessId, groupId, masterId, overdueOnly, todayKey]);

  const activeFilterCount =
    (search ? 1 : 0) +
    (statusSet.size > 0 ? 1 : 0) +
    (employeeId ? 1 : 0) +
    (businessId ? 1 : 0) +
    (groupId ? 1 : 0) +
    (masterId ? 1 : 0) +
    (overdueOnly ? 1 : 0);

  const clearAll = () => {
    setSearch("");
    setStatusSet(new Set());
    setEmployeeId("");
    setBusinessId("");
    setGroupId("");
    setMasterId("");
    setOverdueOnly(false);
  };

  const toggleStatus = (state) => {
    setStatusSet((prev) => {
      const next = new Set(prev);
      if (next.has(state)) next.delete(state);
      else next.add(state);
      return next;
    });
  };

  const subtitle = tasks.length
    ? activeFilterCount > 0
      ? `Showing ${filtered.length} of ${tasks.length} task${tasks.length === 1 ? "" : "s"}`
      : `${tasks.length} task${tasks.length === 1 ? "" : "s"} scheduled`
    : null;

  // Status chips: show only states that exist on this day, plus the synthetic
  // "overdue" overlay if any task on this day qualifies.
  const presentStates = useMemo(() => {
    const set = new Set();
    let anyOverdue = false;
    for (const t of tasks) {
      set.add(taskState(t));
      const dueKey = (t.due_date || "").slice(0, 10);
      if (dueKey && dueKey < todayKey && t.status !== "completed" && t.status !== "cancelled") {
        anyOverdue = true;
      }
    }
    return { set, anyOverdue };
  }, [tasks, todayKey]);

  const statusChips = LEGEND_ENTRIES.filter(
    (e) => e.state !== "overdue" && presentStates.set.has(e.state),
  );

  return (
    <Drawer open={open} onClose={onClose} title={date ? date.format("dddd, DD MMM YYYY") : "Tasks"} subtitle={subtitle}>
      {tasks.length === 0 ? (
        <div className="t-muted text-sm" style={{ padding: 12 }}>
          No tasks for this day.
        </div>
      ) : (
        <div className="col gap-md">
          <div className="card" style={{ padding: 10 }}>
            <div style={{ position: "relative" }}>
              <Icon
                name="search"
                size={12}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--ink-400)",
                  pointerEvents: "none",
                }}
              />
              <input
                className="input"
                placeholder="Search tasks, business, client, employee…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ paddingLeft: 26, fontSize: 13, height: 32 }}
              />
              {search && (
                <button
                  type="button"
                  className="icon-btn"
                  title="Clear search"
                  onClick={() => setSearch("")}
                  style={{
                    position: "absolute",
                    right: 4,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 24,
                    height: 24,
                  }}
                >
                  <Icon name="x" size={12} />
                </button>
              )}
            </div>

            {(statusChips.length > 1 || presentStates.anyOverdue) && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                {statusChips.map((e) => {
                  const active = statusSet.has(e.state);
                  return (
                    <button
                      key={e.state}
                      type="button"
                      className={`chip cal-task ${e.state}`}
                      onClick={() => toggleStatus(e.state)}
                      style={{
                        padding: "3px 9px",
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: "pointer",
                        borderRadius: 999,
                        opacity: statusSet.size === 0 || active ? 1 : 0.45,
                        outline: active ? "2px solid var(--primary-500)" : "none",
                        outlineOffset: active ? -2 : 0,
                      }}
                    >
                      {STATUS_LABELS[e.state]}
                    </button>
                  );
                })}
                {presentStates.anyOverdue && (
                  <button
                    type="button"
                    onClick={() => setOverdueOnly((v) => !v)}
                    className="chip"
                    style={{
                      padding: "3px 9px",
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                      borderRadius: 999,
                      background: overdueOnly ? "var(--danger)" : "var(--danger-50, #fef2f2)",
                      color: overdueOnly ? "#fff" : "var(--danger)",
                      border: "1px solid var(--danger)",
                    }}
                  >
                    Overdue only
                  </button>
                )}
              </div>
            )}

            {(facets.employees.length > 1 ||
              facets.businesses.length > 1 ||
              facets.groups.length > 1 ||
              facets.masters.length > 1) && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                {facets.employees.length > 1 && (
                  <SearchableSelect
                    value={employeeId}
                    onChange={setEmployeeId}
                    options={facets.employees}
                    placeholder="Any employee"
                    searchPlaceholder="Search employees…"
                    width={170}
                  />
                )}
                {facets.businesses.length > 1 && (
                  <SearchableSelect
                    value={businessId}
                    onChange={setBusinessId}
                    options={facets.businesses}
                    placeholder="Any client"
                    searchPlaceholder="Search clients…"
                    width={170}
                  />
                )}
                {facets.groups.length > 1 && (
                  <SearchableSelect
                    value={groupId}
                    onChange={setGroupId}
                    options={facets.groups}
                    placeholder="Any task group"
                    searchPlaceholder="Search groups…"
                    width={170}
                  />
                )}
                {facets.masters.length > 1 && (
                  <SearchableSelect
                    value={masterId}
                    onChange={setMasterId}
                    options={facets.masters}
                    placeholder="Any task master"
                    searchPlaceholder="Search task masters…"
                    width={190}
                  />
                )}
              </div>
            )}

            {activeFilterCount > 0 && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={clearAll}
                  title="Reset all filters"
                >
                  <Icon name="x" size={11} /> Clear filters
                </button>
              </div>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="empty-state" style={{ padding: 16 }}>
              <div className="empty-state-title">No tasks match</div>
              <div className="text-xs">Try adjusting or clearing the filters.</div>
            </div>
          ) : (
            <div className="col gap-sm">
              {filtered.map((t) => {
                const state = taskState(t);
                const dueKey = (t.due_date || "").slice(0, 10);
                const overdue =
                  dueKey && dueKey < todayKey && t.status !== "completed" && t.status !== "cancelled";
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`cal-task ${state} ${overdue ? "overdue" : ""}`}
                    style={{
                      padding: "10px 12px",
                      fontSize: 13,
                      whiteSpace: "normal",
                      textAlign: "left",
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      cursor: "pointer",
                    }}
                    onClick={() => onTaskClick?.(t.id)}
                  >
                    <span style={{ fontWeight: 600 }}>{t.task_master?.name || "Task"}</span>
                    <span style={{ opacity: 0.85, fontSize: 12 }}>
                      {[
                        t.business?.business_name,
                        t.employee?.name,
                        STATUS_LABELS[state] + (overdue ? " · overdue" : ""),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
